import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Megaphone, Search, LogOut } from 'lucide-react';
import { KG } from './desktop-tokens';
import { SimpleCitySelector } from '@/components/simple-city-selector';
import { clearAllStorage } from '@/lib/storage-clear';
import { useApp } from '@/lib/context';
import { apiRequest } from '@/lib/queryClient';
import { TelegramImage } from '@/components/profile/telegram-image';
import { useDetailPanel } from './detail-panel-context';
import {
  useFeedNotificationsCount,
  markFeedNotificationsViewed,
} from '@/components/notifications/notifications-feed-panel';

// (The animation is provided natively by the animated megaphone.webp file —
//  no CSS keyframes needed. When there are no unread feeds we render a
//  static lucide Megaphone icon instead so it doesn't move on its own.)

export interface DesktopTopBarAction {
  label: string;
  icon?: ReactNode;
  primary?: boolean;
  onClick?: () => void;
}

// ── i18n del modale di logout ──────────────────────────────────────────────
type LogoutLang = 'it' | 'en' | 'es' | 'de' | 'pt' | 'fr';
interface LogoutStrings { title: string; body: string; cancel: string; confirm: string }
const LOGOUT_DICT: Record<LogoutLang, LogoutStrings> = {
  it: { title: 'Uscire da Kinkygram?', body: 'Verrai disconnesso da questo dispositivo e tornerai alla home.', cancel: 'Annulla', confirm: 'Logout' },
  en: { title: 'Log out of Kinkygram?', body: "You'll be signed out of this device and return to the home screen.", cancel: 'Cancel', confirm: 'Log out' },
  es: { title: '¿Salir de Kinkygram?', body: 'Se cerrará tu sesión en este dispositivo y volverás al inicio.', cancel: 'Cancelar', confirm: 'Cerrar sesión' },
  de: { title: 'Von Kinkygram abmelden?', body: 'Du wirst auf diesem Gerät abgemeldet und kehrst zur Startseite zurück.', cancel: 'Abbrechen', confirm: 'Abmelden' },
  pt: { title: 'Sair do Kinkygram?', body: 'Você será desconectado deste dispositivo e voltará para a tela inicial.', cancel: 'Cancelar', confirm: 'Sair' },
  fr: { title: 'Se déconnecter de Kinkygram ?', body: "Vous serez déconnecté de cet appareil et reviendrez à l'accueil.", cancel: 'Annuler', confirm: 'Déconnexion' },
};
function logoutStrings(language?: string): LogoutStrings {
  const raw = (language || 'en').toLowerCase().substring(0, 2);
  return LOGOUT_DICT[(raw in LOGOUT_DICT ? raw : 'en') as LogoutLang];
}

interface DesktopTopBarProps {
  title: string;
  subtitle?: string;
  actions?: DesktopTopBarAction[];
  showSearch?: boolean;
  hasNotification?: boolean;
  onNotificationsClick?: () => void;
  onSearchClick?: () => void;
}

export function DesktopTopBar({
  title,
  subtitle,
  actions = [],
  showSearch = true,
  hasNotification = false,
  onNotificationsClick,
  onSearchClick,
}: DesktopTopBarProps) {
  const { user } = useApp();
  const lt = logoutStrings((user as any)?.language);
  const { open: openDetailPanel } = useDetailPanel();
  const [showLogout, setShowLogout] = useState(false);
  const confirmLogout = async () => {
    // 1. Clear the session.
    try { clearAllStorage(true); } catch { /* ignore */ }
    // 2. Drop SW caches + unregister the service worker. After frequent
    //    deploys a stale cached build serves dead chunk hashes (404 on
    //    index-*.js/css) and the home fails to load — clearing them forces a
    //    fresh fetch of index.html.
    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
    } catch { /* ignore */ }
    // 3. Force a real navigation+reload to the (renovated) landing. Setting
    //    href to the current URL is a no-op, so reload() in that case.
    if (window.location.pathname === '/' && !window.location.search) {
      window.location.reload();
    } else {
      window.location.href = '/';
    }
  };
  const feedCount = useFeedNotificationsCount();
  const hasFeedNotifications = hasNotification || feedCount > 0;
  const handleNotificationsClick = onNotificationsClick
    ?? (() => {
      markFeedNotificationsViewed();
      openDetailPanel({ kind: 'notifications', id: 'me' });
    });
  return (
    <header
      style={{
        height: 64,
        padding: '0 24px',
        borderBottom: `1px solid ${KG.border}`,
        background: KG.bg,
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        position: 'sticky',
        top: 0,
        zIndex: 30,
      }}
    >
      <div style={{ minWidth: 0, flexShrink: 0 }}>
        <div
          style={{
            fontSize: 18,
            fontWeight: 700,
            lineHeight: 1.2,
            letterSpacing: '-0.01em',
            color: KG.text,
          }}
        >
          {title}
        </div>
        {subtitle && (
          <div style={{ fontSize: 12, color: KG.hint, marginTop: 2 }}>{subtitle}</div>
        )}
      </div>

      {showSearch && <DesktopSearch />}

      <div
        style={{
          // Fill the remaining width: city + actions + bell stay right after the
          // search; only logout is pushed to the far right (margin-left:auto on it).
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <div style={{ width: 200 }}>
          <SimpleCitySelector
            variant="desktop"
            defaultCity={(user?.city as string) || ''}
          />
        </div>
        {actions.map((a, i) => (
          <button
            key={`${a.label}-${i}`}
            type="button"
            onClick={a.onClick}
            style={{
              height: 38,
              padding: '0 14px',
              borderRadius: 10,
              background: a.primary ? KG.blue : KG.panel,
              border: `1px solid ${a.primary ? KG.blue : KG.border}`,
              color: a.primary ? '#fff' : KG.text2,
              fontSize: 13,
              fontWeight: 600,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            {a.icon}
            {a.label}
          </button>
        ))}
        <button
          type="button"
          onClick={handleNotificationsClick}
          aria-label="Feeds"
          style={{
            width: 38,
            height: 38,
            borderRadius: 10,
            background: KG.panel,
            border: `1px solid ${KG.border}`,
            color: KG.text2,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          {hasFeedNotifications ? (
            // Animated megaphone.webp loops natively — used only when there is
            // something unread, so the icon "rings" until the panel is opened.
            <img
              src="/assets/emoji/megaphone.webp"
              alt="📣"
              style={{ width: 18, height: 18, userSelect: 'none', WebkitUserSelect: 'none' }}
              draggable={false}
            />
          ) : (
            // Static fallback — lucide Megaphone — when there's nothing new.
            <Megaphone size={16} strokeWidth={1.7} />
          )}
          {hasFeedNotifications && (
            <span
              aria-hidden
              style={{
                position: 'absolute',
                top: -3,
                right: -3,
                width: 10,
                height: 10,
                borderRadius: 99,
                background: KG.red,
                boxShadow: `0 0 0 2px ${KG.bg}`,
              }}
            />
          )}
        </button>

        {/* Logout — pushed to the far right; opens a confirm modal */}
        <button
          type="button"
          onClick={() => setShowLogout(true)}
          aria-label={lt.confirm}
          title={lt.confirm}
          style={{
            marginLeft: 'auto',
            width: 38,
            height: 38,
            borderRadius: 10,
            background: KG.panel,
            border: `1px solid ${KG.border}`,
            color: KG.text2,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          <LogOut size={16} strokeWidth={1.8} />
        </button>
      </div>

      {showLogout && (
        <div
          onClick={() => setShowLogout(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 2000,
            background: 'rgba(6,8,12,0.6)', backdropFilter: 'blur(2px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: 360, background: KG.panel,
              border: `1px solid ${KG.borderHi}`, borderRadius: 16, padding: 22,
              boxShadow: '0 30px 70px -20px rgba(0,0,0,0.7)', color: KG.text,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
              <span style={{ width: 38, height: 38, borderRadius: 10, flexShrink: 0, background: 'rgba(236,57,66,0.14)', border: '1px solid rgba(236,57,66,0.35)', color: KG.red, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                <LogOut size={18} strokeWidth={1.9} />
              </span>
              <div style={{ fontSize: 16, fontWeight: 700 }}>{lt.title}</div>
            </div>
            <div style={{ fontSize: 13, color: KG.text2, lineHeight: 1.5, marginBottom: 18 }}>
              {lt.body}
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setShowLogout(false)}
                style={{ height: 40, padding: '0 16px', borderRadius: 10, background: 'transparent', border: `1px solid ${KG.border}`, color: KG.text2, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
              >
                {lt.cancel}
              </button>
              <button
                type="button"
                onClick={confirmLogout}
                style={{ height: 40, padding: '0 18px', borderRadius: 10, background: KG.red, border: 'none', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}
              >
                {lt.confirm}
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

/**
 * Live user search for the desktop top bar. Debounced fuzzy lookup on
 * /api/users/search?q=…; selecting a result opens the profile in the
 * DetailPanel.
 */
function DesktopSearch() {
  const { open: openDetailPanel } = useDetailPanel();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res: any = await apiRequest(
          'GET',
          `/api/users/search?q=${encodeURIComponent(q)}`
        );
        if (!cancelled) {
          setResults(res?.users || []);
          setOpen(true);
        }
      } catch {
        if (!cancelled) setResults([]);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const selectUser = (u: any) => {
    openDetailPanel({ kind: 'profile', id: u.id });
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  return (
    <div ref={boxRef} style={{ position: 'relative', flex: 1, maxWidth: 480, marginLeft: 24 }}>
      <div
        style={{
          height: 38,
          borderRadius: 10,
          background: KG.panel,
          border: `1px solid ${KG.border}`,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '0 12px',
          color: KG.hint,
        }}
      >
        <Search size={16} strokeWidth={1.6} />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && results[0]) selectUser(results[0]);
            else if (e.key === 'Escape') setOpen(false);
          }}
          placeholder="Cerca @username…"
          style={{
            flex: 1,
            background: 'transparent',
            border: 'none',
            outline: 'none',
            color: KG.text,
            fontSize: 13,
            fontFamily: 'inherit',
          }}
        />
      </div>
      {open && results.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: 44,
            left: 0,
            right: 0,
            background: KG.panel,
            border: `1px solid ${KG.border}`,
            borderRadius: 10,
            overflow: 'hidden',
            zIndex: 40,
            boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
          }}
        >
          {results.map((u) => (
            <button
              key={u.id}
              type="button"
              onClick={() => selectUser(u)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                width: '100%',
                padding: '8px 12px',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                textAlign: 'left',
                color: KG.text,
                fontFamily: 'inherit',
              }}
            >
              <span
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 999,
                  overflow: 'hidden',
                  flexShrink: 0,
                  display: 'inline-flex',
                }}
              >
                <TelegramImage
                  photos={u.photos}
                  userId={u.id}
                  username={u.username}
                  variant="avatar"
                  alt={u.username}
                  className="w-full h-full"
                />
              </span>
              <span style={{ fontSize: 13, fontWeight: 600 }}>{u.username}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default DesktopTopBar;
